import React, { useState, useMemo, useEffect } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/shadcn/button'
import { Input } from '@/components/shadcn/input'
import { Badge } from '@/components/shadcn/badge'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/shadcn/select'
import { DataTable } from '@/components/shadcn/data-table'
import { createMemoriesColumns, type MemoryMessage } from './memories-columns'
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/shadcn/pagination'
import { Search, Brain, Trash2, Filter, Eye } from 'lucide-react'
import { getMemory, countMemory, deleteMemoryLibrary, deleteMemoryMessages } from '@/lib/api/memory'

import { Toggle } from '@/components/shadcn/toggle'
import { Calendar } from '@/components/shadcn/calendar'
import { format } from 'date-fns'
import { Calendar as CalendarIcon } from 'lucide-react'

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/shadcn/dialog'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/shadcn/popover'

import { useToast } from '@/hooks/use-toast'



export default function MemoriesPage() {
  const { t } = useTranslation('common')
  const queryClient = useQueryClient()

  const { toast } = useToast()
  // 搜索框输入（本地状态）与实际用于查询的关键词（点击按钮后应用）
  const [searchInput, setSearchInput] = useState('')
  const [searchKeywords, setSearchKeywords] = useState('')

  const url = new URL(window.location.href)
  const initialMemoryId = url.searchParams.get('memory_id') || ''
  const [selectedLibraryId, setSelectedLibraryId] = useState(initialMemoryId)

  const [selectedRoles, setSelectedRoles] = useState<string[]>([])
  const [startDate, setStartDate] = useState<Date | undefined>(undefined)
  const [endDate, setEndDate] = useState<Date | undefined>(undefined)
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState<number>(10)
  const [deleteMsgId, setDeleteMsgId] = useState<string | null>(null)
  const [deleteMsgStep, setDeleteMsgStep] = useState<1 | 2 | 0>(0)
  const [deleteLibStep, setDeleteLibStep] = useState<1 | 2 | 0>(0)
  const [filterOpen, setFilterOpen] = useState(false)
  const [viewOpen, setViewOpen] = useState(false)
  const [selectedMessages, setSelectedMessages] = useState<string[]>([])
  const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({})





  // 组合查询参数
  const commonQuery = {
    memory_id: selectedLibraryId || undefined,
    limit: limit || undefined,
    page,
    start_time_iso: startDate?.toISOString(),
    end_time_iso: endDate?.toISOString(),
    roles: selectedRoles.length > 0 ? (selectedRoles as any) : undefined,
    search_keywords: searchKeywords || undefined,
  }

  const { data: countData } = useQuery({
    queryKey: ['memory-count', commonQuery],
    queryFn: () => countMemory(commonQuery as any),
    enabled: !!selectedLibraryId,
  })

  const { data: memoryData, isLoading: loadingMessages } = useQuery({
    queryKey: ['memory-data', commonQuery],
    queryFn: () => getMemory(commonQuery as any),
    enabled: !!selectedLibraryId,
  })

  const refreshLists = () => {
    queryClient.invalidateQueries({ queryKey: ['memory-data'] })
    queryClient.invalidateQueries({ queryKey: ['memory-count'] })
  }

  const handleDeleteMessage = async (messageId: string) => {
    if (!selectedLibraryId) return
    const results = await deleteMemoryMessages([{ message_id: messageId, memory_id: selectedLibraryId }])
    setDeleteMsgId(null)
    setDeleteMsgStep(0)
    // 刷新列表
    refreshLists()
    const okCount = results.filter(r => r === 'ok').length
    toast({ title: t('memory.detail.toast.deleteComplete'), description: t('memory.detail.toast.deleteSuccess', { total: results.length, success: okCount }), })
  }

  const handleDeleteLibrary = async () => {
    if (!selectedLibraryId) return
    const result = await deleteMemoryLibrary(selectedLibraryId)
    setDeleteLibStep(0)
    if (result === 'ok') {
      setSelectedLibraryId('')
      // 刷新库列表与数据
      queryClient.invalidateQueries({ queryKey: ['memory-library-ids'] })
      refreshLists()
      toast({ title: t('memory.detail.toast.deleteVaultSuccess'), description: t('memory.detail.toast.deleteVaultSuccess') })
    } else {
      toast({ title: t('memory.detail.toast.deleteVaultError'), description: t('memory.detail.toast.deleteVaultError'), })
    }
  }



  // 将消息数据转换为 DataTable 需要的格式
  const tableData = useMemo((): MemoryMessage[] => {
    if (!memoryData?.messages) return []
    return memoryData.messages.map(msg => ({
      id: msg.id,
      time_iso: msg.time_iso,
      role: msg.role,
      content: msg.content || []
    }))
  }, [memoryData?.messages])

  // 创建列定义
  const memoriesColumns = useMemo(() => 
    createMemoriesColumns(t), 
    [t]
  )

  // 处理行选择变化
  const handleRowSelectionChange = (updaterOrValue: any) => {
    const newRowSelection = typeof updaterOrValue === 'function' 
      ? updaterOrValue(rowSelection) 
      : updaterOrValue
    setRowSelection(newRowSelection)
    const selectedIds = Object.keys(newRowSelection).filter(key => newRowSelection[key])
    setSelectedMessages(selectedIds)
  }

  // 批量删除选中的消息
  const handleBatchDelete = async () => {
    if (!selectedLibraryId || selectedMessages.length === 0) return
    
    const results = await deleteMemoryMessages(
      selectedMessages.map(messageId => ({ 
        message_id: messageId, 
        memory_id: selectedLibraryId 
      }))
    )
    
    setSelectedMessages([])
    setRowSelection({})
    refreshLists()
    
    const okCount = results.filter(r => r === 'ok').length
    toast({ 
      title: t('memory.detail.toast.batchDeleteComplete'), 
      description: t('memory.detail.toast.deleteSuccess', { total: results.length, success: okCount })
    })
  }

  // 监听删除消息事件
  useEffect(() => {
    const handleDeleteMessage = (event: CustomEvent) => {
      const { messageId } = event.detail
      setDeleteMsgId(messageId)
      setDeleteMsgStep(1)
    }

    window.addEventListener('deleteMessage', handleDeleteMessage as EventListener)
    return () => {
      window.removeEventListener('deleteMessage', handleDeleteMessage as EventListener)
    }
  }, [])

  return (
    <div className="space-y-6">

      {/* <div>
        <p className="text-sm text-muted-foreground mb-6">
          查看、搜索和管理存储的记忆数据
        </p>
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="w-64">
              <Select
                value={selectedLibraryId}
                onValueChange={(v) => { setSelectedLibraryId(v); setPage(1) }}
                disabled={loadingLibIds}
              >
                <SelectTrigger>
                  <SelectValue placeholder={libIdsError ? '加载失败' : (loadingLibIds ? '加载库列表...' : '选择记忆库')} />
                </SelectTrigger>
                <SelectContent>
                  {(libraryIds || []).map((id) => (
                    <SelectItem key={id} value={id}>
                      {id}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button>
              <Plus className="mr-2 h-4 w-4" />
              添加记忆
            </Button>
          </div>
        </div>
      </div> */}
      {/* Stats */}
      <div className="grid gap-6 md:grid-cols-1 lg:grid-cols-2 pt-2">
        <div className="border rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium">{t('memory.detail.stats.totalMemories')}</h3>
            <Brain className="h-4 w-4 text-muted-foreground" />
          </div>
          <div className="text-2xl font-bold">{countData?.count ?? 0}</div>
          <p className="text-xs text-muted-foreground">
            {t('memory.detail.stats.last24Hours')} 3 条
          </p>
        </div>

        {/* 暂时隐藏平均置信度和活跃记忆统计 */}
        {/* <div className="border rounded-lg p-4 opacity-50">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium">{t('memory.detail.stats.avgConfidence')}</h3>
            <Badge className="h-4 w-4 text-muted-foreground" />
          </div>
          <div className="text-2xl font-bold text-muted-foreground">—</div>
          <p className="text-xs text-muted-foreground">{t('memory.detail.stats.notSupported')}</p>
        </div>

        <div className="border rounded-lg p-4 opacity-50">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium">{t('memory.detail.stats.activeMemories')}</h3>
            <Badge className="h-4 w-4 text-muted-foreground" />
          </div>
          <div className="text-2xl font-bold text-muted-foreground">—</div>
          <p className="text-xs text-muted-foreground">{t('memory.detail.stats.notSupported')}</p>
        </div> */}
      </div>

      {/* Search and Filters */}
      <div>
        {/* <p className="text-sm text-muted-foreground mb-4">
          使用语义搜索找到相关的记忆内容
        </p> */}
        <div className="flex gap-4 items-center">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-[10px] h-4 w-4 text-muted-foreground" />
              <Input
                placeholder={t('memory.detail.search.placeholder')}
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    setPage(1)
                    setSearchKeywords(searchInput)
                  }
                }}
                className="pl-10"
              />
            </div>
          </div>
          <Button
            onClick={() => {
              setPage(1)
              setSearchKeywords(searchInput)
            }}
          >
            <Search className="mr-2 h-4 w-4" />
            {t('memory.detail.search.button')}
          </Button>
          
          {/* 筛选按钮 */}
          <Popover open={filterOpen} onOpenChange={setFilterOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline">
                <Filter className="mr-2 h-4 w-4" />
                {t('memory.detail.search.filter')}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80" align="end">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="font-medium">{t('memory.detail.search.options')}</h4>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setSelectedRoles([])
                      setStartDate(undefined)
                      setEndDate(undefined)
                    }}
                  >
                    {t('memory.detail.search.reset')}
                  </Button>
                </div>

                {/* 角色筛选 */}
                <div>
                  <div className="text-sm font-medium mb-2">{t('memory.detail.search.role')}</div>
                  <div className="flex flex-wrap gap-2">
                    {['user','assistant','tool','function'].map((role) => (
                      <Toggle
                        key={role}
                        pressed={selectedRoles.includes(role)}
                        onPressedChange={(pressed: boolean) => {
                          setSelectedRoles(prev => 
                            pressed 
                              ? [...prev, role] 
                              : prev.filter(r => r !== role)
                          )
                        }}
                        size="sm"
                        variant="outline"
                      >
                        {role}
                      </Toggle>
                    ))}
                  </div>
                </div>

                {/* 时间范围 */}
                <div className="grid gap-3">
                  <div>
                    <div className="text-sm font-medium mb-2">{t('memory.detail.search.startTime')}</div>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className="w-full justify-start text-left font-normal"
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {startDate ? format(startDate, "PPP") : <span>{t('memory.detail.search.selectStartTime')}</span>}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={startDate}
                          onSelect={setStartDate}
                          initialFocus
                          className="rounded-md border-0"
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div>
                    <div className="text-sm font-medium mb-2">{t('memory.detail.search.endTime')}</div>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className="w-full justify-start text-left font-normal"
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {endDate ? format(endDate, "PPP") : <span>{t('memory.detail.search.selectEndTime')}</span>}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-80 p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={endDate}
                          onSelect={setEndDate}
                          initialFocus
                          className="rounded-md border-0"
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>
              </div>
            </PopoverContent>
          </Popover>

          {/* 视图按钮 */}
          <Popover open={viewOpen} onOpenChange={setViewOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline">
                <Eye className="mr-2 h-4 w-4" />
                {t('memory.detail.search.view')}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-64" align="end">
              <div className="space-y-4">
                <h4 className="font-medium">{t('memory.detail.search.viewOptions')}</h4>
                
                {/* 每页数量 */}
                <div>
                  <div className="text-sm font-medium mb-2">{t('memory.detail.search.itemsPerPage')}</div>
                  <Select value={String(limit)} onValueChange={(v) => { setPage(1); setLimit(Number(v)) }}>
                    <SelectTrigger>
                      <SelectValue placeholder={t('memory.detail.search.selectCount')} />
                    </SelectTrigger>
                    <SelectContent>
                      {[5,10,20,50,100].map(n => (
                        <SelectItem key={n} value={String(n)}>{n}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </PopoverContent>
          </Popover>

          {/* 批量删除按钮 */}
          {selectedMessages.length > 0 && (
            <Button 
              variant="destructive" 
              onClick={handleBatchDelete}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              {t('memory.detail.batchDelete', { count: selectedMessages.length })}
            </Button>
          )}
        </div>

      </div>

      {/* Messages Table */}
      <div>

        <div className="space-y-2">
          <DataTable 
            columns={memoriesColumns} 
            data={tableData} 
            rowSelection={rowSelection}
            onRowSelectionChange={handleRowSelectionChange}
          />

          {/* Pagination */}
          <div className="flex items-center justify-between pt-4">
            <div className="text-sm text-muted-foreground whitespace-nowrap pl-1">
              {t('memory.detail.pagination.total', { count: countData?.count ?? 0 })}
            </div>
            <Pagination className="justify-end">
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious 
                    href="#"
                    onClick={(e) => {
                      e.preventDefault()
                      if (!selectedLibraryId || page <= 1 || loadingMessages) return
                      setPage((p) => Math.max(1, p - 1))
                    }}
                    className={(!selectedLibraryId || page <= 1 || loadingMessages) ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                  />
                </PaginationItem>
                
                {/* 页码显示 */}
                {countData?.total_pages && countData.total_pages > 1 && (
                  <>
                    {/* 显示页码 */}
                    {(() => {
                      const totalPages = countData.total_pages || 1
                      const pages: number[] = []
                      
                      if (totalPages <= 7) {
                        // 总页数少于等于7页，显示所有页码
                        for (let i = 1; i <= totalPages; i++) {
                          pages.push(i)
                        }
                      } else {
                        // 总页数大于7页，智能显示页码
                        if (page <= 4) {
                          // 当前页在前4页，显示前6页 + 最后一页
                          for (let i = 1; i <= 6; i++) {
                            pages.push(i)
                          }
                          if (totalPages > 6) {
                            pages.push(totalPages)
                          }
                        } else if (page >= totalPages - 3) {
                          // 当前页在后4页，显示第1页 + 后6页
                          pages.push(1)
                          for (let i = totalPages - 5; i <= totalPages; i++) {
                            if (i > 1) pages.push(i)
                          }
                        } else {
                          // 当前页在中间，显示第1页 + 当前页前后2页 + 最后一页
                          pages.push(1)
                          for (let i = page - 2; i <= page + 2; i++) {
                            if (i > 1 && i < totalPages) pages.push(i)
                          }
                          pages.push(totalPages)
                        }
                      }
                      
                      return pages.map((pageNum, index) => {
                        const showEllipsis = index > 0 && pageNum - pages[index - 1] > 1
                        return (
                          <React.Fragment key={pageNum}>
                            {showEllipsis && (
                              <PaginationItem>
                                <span className="flex h-9 w-9 items-center justify-center">
                                  <span className="sr-only">{t('memory.detail.pagination.morePages')}</span>
                                  ...
                                </span>
                              </PaginationItem>
                            )}
                            <PaginationItem>
                              <PaginationLink
                                href="#"
                                isActive={pageNum === page}
                                onClick={(e) => {
                                  e.preventDefault()
                                  if (!selectedLibraryId || loadingMessages) return
                                  setPage(pageNum)
                                }}
                                className="cursor-pointer"
                              >
                                {pageNum}
                              </PaginationLink>
                            </PaginationItem>
                          </React.Fragment>
                        )
                      })
                    })()}
                  </>
                )}
                
                <PaginationItem>
                  <PaginationNext 
                    href="#"
                    onClick={(e) => {
                      e.preventDefault()
                      if (!selectedLibraryId || (countData?.total_pages ? page >= (countData?.total_pages || 1) : false) || loadingMessages) return
                      setPage((p) => p + 1)
                    }}
                    className={(!selectedLibraryId || (countData?.total_pages ? page >= (countData?.total_pages || 1) : false) || loadingMessages) ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </div>
        </div>
      </div>

      {/* 删除记忆库 */}
      <div className="border border-destructive/20 rounded-lg p-4">
        <div className="mb-4">
          <h3 className="text-lg font-medium">{t('memory.detail.deleteVault.title')}</h3>
          <p className="text-sm text-muted-foreground">{t('memory.detail.deleteVault.description')}</p>
        </div>
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="text-sm text-muted-foreground">
            {selectedLibraryId ? (
              <>
                <div>{t('memory.detail.deleteVault.currentSelection')}<span className="font-medium text-foreground">{selectedLibraryId}</span></div>
                <div>{t('memory.detail.deleteVault.totalRecords')}{countData?.count ?? 0}{typeof countData?.total_pages === 'number' && ` · ${t('memory.detail.deleteVault.totalPages')}${countData?.total_pages}`}</div>
              </>
            ) : t('memory.detail.deleteVault.pleaseSelect')}
          </div>
          <Button
            variant="destructive"
            disabled={!selectedLibraryId}
            onClick={() => setDeleteLibStep(1)}
          >
            <Trash2 className="h-4 w-4 mr-2" /> {t('memory.detail.deleteVault.button')}
          </Button>
        </div>
      </div>

      {/* 双重确认 - 删除单条消息 Step 1 */}
      <Dialog open={deleteMsgStep === 1} onOpenChange={(o) => !o && setDeleteMsgStep(0)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('memory.detail.confirmDelete.message.title')}</DialogTitle>
            <DialogDescription>{t('memory.detail.confirmDelete.message.description', { messageId: deleteMsgId })}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteMsgStep(0)}>{t('memory.detail.confirmDelete.message.cancel')}</Button>
            <Button variant="destructive" onClick={() => setDeleteMsgStep(2)}>{t('memory.detail.confirmDelete.message.continue')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 双重确认 - 删除单条消息 Step 2 */}
      <Dialog open={deleteMsgStep === 2} onOpenChange={(o) => !o && setDeleteMsgStep(0)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('memory.detail.confirmDelete.message.confirmTitle')}</DialogTitle>
            <DialogDescription>{t('memory.detail.confirmDelete.message.confirmDescription', { messageId: deleteMsgId })}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteMsgStep(0)}>{t('memory.detail.confirmDelete.message.cancel')}</Button>
            <Button variant="destructive" onClick={() => deleteMsgId && handleDeleteMessage(deleteMsgId)}>{t('memory.detail.confirmDelete.message.confirmDelete')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 双重确认 - 删除记忆库 Step 1 */}
      <Dialog open={deleteLibStep === 1} onOpenChange={(o) => !o && setDeleteLibStep(0)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('memory.detail.confirmDelete.vault.title')}</DialogTitle>
            <DialogDescription>
              {t('memory.detail.confirmDelete.vault.description', { 
                vaultId: selectedLibraryId || '-', 
                count: countData?.count ?? 0, 
                pages: typeof countData?.total_pages === 'number' ? `共 ${countData?.total_pages} 页` : '未分页'
              })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteLibStep(0)}>{t('memory.detail.confirmDelete.vault.cancel')}</Button>
            <Button variant="destructive" onClick={() => setDeleteLibStep(2)}>{t('memory.detail.confirmDelete.vault.continue')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 双重确认 - 删除记忆库 Step 2 */}
      <Dialog open={deleteLibStep === 2} onOpenChange={(o) => !o && setDeleteLibStep(0)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('memory.detail.confirmDelete.vault.confirmTitle')}</DialogTitle>
            <DialogDescription>
              {t('memory.detail.confirmDelete.vault.confirmDescription', { 
                vaultId: selectedLibraryId || '-', 
                count: countData?.count ?? 0 
              })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteLibStep(0)}>{t('memory.detail.confirmDelete.vault.cancel')}</Button>
            <Button variant="destructive" onClick={handleDeleteLibrary}>{t('memory.detail.confirmDelete.vault.confirmDelete')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  )
}
